/**
 * RummyAI — AI opponent for Indian Rummy
 *
 * Difficulty levels:
 * - Easy: random draw source, discard highest unmelded card
 * - Medium: draw from discard if it completes a meld, discard least useful card
 * - Hard: track opponent discards, avoid discarding cards they might need, optimal meld detection
 */

import {
  Card, RummyDifficulty, RummyAction, VisibleRummyState,
  RANK_VALUES, CARD_POINTS,
} from './types';
import { RummyEngine } from './RummyEngine';

export class RummyAI {
  private difficulty: RummyDifficulty;
  private discardTracker: Map<string, Card[]>;
  private engine: RummyEngine;

  constructor(difficulty: RummyDifficulty) {
    this.difficulty = difficulty;
    this.discardTracker = new Map();
    this.engine = new RummyEngine();
  }

  /**
   * Decide what action to take given the current visible state.
   */
  async getAction(
    state: VisibleRummyState,
    playerId: string,
    hand: Card[],
  ): Promise<RummyAction> {
    // Add artificial delay based on difficulty
    const delay = this.difficulty === 'easy' ? 500 : this.difficulty === 'medium' ? 800 : 1200;
    await new Promise(resolve => setTimeout(resolve, delay));

    if (state.turnPhase === 'draw') {
      return this.decideDrawAction(state, hand);
    }

    // Discard phase — check if we should declare
    if (hand.length === 14) {
      const shouldDeclare = this.shouldDeclare(hand);
      if (shouldDeclare) {
        return { type: 'declare' };
      }
      return this.decideDiscardAction(state, hand);
    }

    return this.decideDiscardAction(state, hand);
  }

  // ===========================================================================
  // Draw Decision
  // ===========================================================================

  private decideDrawAction(state: VisibleRummyState, hand: Card[]): RummyAction {
    switch (this.difficulty) {
      case 'easy':
        return this.easyDrawDecision(state);
      case 'medium':
        return this.mediumDrawDecision(state, hand);
      case 'hard':
        return this.hardDrawDecision(state, hand);
      default:
        return { type: 'draw-stock' };
    }
  }

  private easyDrawDecision(_state: VisibleRummyState): RummyAction {
    // Easy: randomly pick stock or discard
    return Math.random() > 0.5 ? { type: 'draw-stock' } : { type: 'draw-discard' };
  }

  private mediumDrawDecision(state: VisibleRummyState, hand: Card[]): RummyAction {
    // Medium: draw from discard if the top card helps form a meld
    const discardTop = state.discardTop;
    if (discardTop && this.cardHelpsHand(discardTop, hand)) {
      return { type: 'draw-discard' };
    }
    return { type: 'draw-stock' };
  }

  private hardDrawDecision(state: VisibleRummyState, hand: Card[]): RummyAction {
    // Hard: draw from discard only if it significantly improves hand
    const discardTop = state.discardTop;
    if (discardTop) {
      const currentScore = this.evaluateHand(hand);
      const withDiscard = [...hand, discardTop];
      // Simulate removing worst card
      const worstIdx = this.findWorstCardIndex(withDiscard);
      const simulated = [...withDiscard.slice(0, worstIdx), ...withDiscard.slice(worstIdx + 1)];
      const newScore = this.evaluateHand(simulated);

      if (newScore > currentScore + 5) {
        return { type: 'draw-discard' };
      }
    }
    return { type: 'draw-stock' };
  }

  // ===========================================================================
  // Discard Decision
  // ===========================================================================

  private decideDiscardAction(_state: VisibleRummyState, hand: Card[]): RummyAction {
    switch (this.difficulty) {
      case 'easy':
        return this.easyDiscardDecision(hand);
      case 'medium':
        return this.mediumDiscardDecision(hand);
      case 'hard':
        return this.hardDiscardDecision(_state, hand);
      default:
        return this.easyDiscardDecision(hand);
    }
  }

  private easyDiscardDecision(hand: Card[]): RummyAction {
    // Easy: discard highest point unmelded card (with some randomness)
    const sorted = [...hand].sort((a, b) => {
      if (a.isJoker || a.isWildJoker) return 1;
      if (b.isJoker || b.isWildJoker) return -1;
      return CARD_POINTS[b.rank] - CARD_POINTS[a.rank];
    });

    // Sometimes pick a random card instead of the worst
    if (Math.random() < 0.3) {
      const randomIdx = Math.floor(Math.random() * hand.length);
      return { type: 'discard', cardId: hand[randomIdx].id };
    }

    return { type: 'discard', cardId: sorted[0].id };
  }

  private mediumDiscardDecision(hand: Card[]): RummyAction {
    // Medium: discard the least useful card (not part of potential melds)
    const cardId = this.findLeastUsefulCard(hand);
    return { type: 'discard', cardId };
  }

  private hardDiscardDecision(state: VisibleRummyState, hand: Card[]): RummyAction {
    // Hard: avoid discarding cards opponents might need
    const cardId = this.findSafestDiscard(state, hand);
    return { type: 'discard', cardId };
  }

  // ===========================================================================
  // Declaration Decision
  // ===========================================================================

  private shouldDeclare(hand: Card[]): boolean {
    const result = this.engine.findBestDeclaration(hand);
    return result.valid;
  }

  // ===========================================================================
  // Hand Evaluation Helpers
  // ===========================================================================

  private cardHelpsHand(card: Card, hand: Card[]): boolean {
    if (card.isJoker || card.isWildJoker) return true;

    // Check if card completes a sequence
    const sameSuit = hand.filter(c => c.suit === card.suit && !c.isJoker && !c.isWildJoker);
    for (const existing of sameSuit) {
      const diff = Math.abs(RANK_VALUES[existing.rank] - RANK_VALUES[card.rank]);
      if (diff === 1 || diff === 2) {
        // Check if there's a third card to complete the sequence
        const needed = RANK_VALUES[card.rank] + (RANK_VALUES[existing.rank] > RANK_VALUES[card.rank] ? -1 : 1);
        if (sameSuit.some(c => RANK_VALUES[c.rank] === needed)) {
          return true;
        }
        if (diff === 1) {
          // Two consecutive cards — close to a sequence
          return true;
        }
      }
    }

    // Check if card completes a set
    const sameRank = hand.filter(c => c.rank === card.rank && c.suit !== card.suit && !c.isJoker);
    if (sameRank.length >= 2) return true;

    return false;
  }

  private evaluateHand(hand: Card[]): number {
    // Higher score = better hand
    let score = 0;

    // Find potential melds
    const melds = this.engine.findAllPossibleMelds(hand);
    const pureSequences = melds.filter(m => m.type === 'sequence' && m.isPure);
    const impureSequences = melds.filter(m => m.type === 'sequence' && !m.isPure);
    const sets = melds.filter(m => m.type === 'set');

    score += pureSequences.length * 20;
    score += impureSequences.length * 10;
    score += sets.length * 8;

    // Jokers are valuable
    const jokerCount = hand.filter(c => c.isJoker || c.isWildJoker).length;
    score += jokerCount * 15;

    // Penalize high-point unmelded cards
    const meldedCardIds = new Set<string>();
    for (const meld of melds) {
      for (const card of meld.cards) {
        meldedCardIds.add(card.id);
      }
    }
    const unmelded = hand.filter(c => !meldedCardIds.has(c.id));
    for (const card of unmelded) {
      if (!card.isJoker && !card.isWildJoker) {
        score -= CARD_POINTS[card.rank];
      }
    }

    return score;
  }

  private findWorstCardIndex(hand: Card[]): number {
    let worstIdx = 0;
    let worstScore = Infinity;

    for (let i = 0; i < hand.length; i++) {
      const card = hand[i];
      // Never discard jokers
      if (card.isJoker || card.isWildJoker) continue;

      const without = [...hand.slice(0, i), ...hand.slice(i + 1)];
      const score = this.evaluateHand(without);
      const cardValue = CARD_POINTS[card.rank];

      // Lower score when removed = card is more useful, keep it
      // Higher score when removed = card is less useful, discard it
      const usefulness = this.evaluateHand(hand) - score;
      const discardScore = usefulness - cardValue * 0.5;

      if (discardScore < worstScore) {
        worstScore = discardScore;
        worstIdx = i;
      }
    }

    return worstIdx;
  }

  private findLeastUsefulCard(hand: Card[]): string {
    let leastUsefulId = hand[0].id;
    let leastUsefulScore = Infinity;

    for (const card of hand) {
      if (card.isJoker || card.isWildJoker) continue;

      const without = hand.filter(c => c.id !== card.id);
      const scoreWithout = this.evaluateHand(without);
      const scoreWith = this.evaluateHand(hand);
      const usefulness = scoreWith - scoreWithout;

      // Prefer discarding high-point cards that aren't useful
      const adjustedScore = usefulness - CARD_POINTS[card.rank] * 0.3;

      if (adjustedScore < leastUsefulScore) {
        leastUsefulScore = adjustedScore;
        leastUsefulId = card.id;
      }
    }

    return leastUsefulId;
  }

  private findSafestDiscard(state: VisibleRummyState, hand: Card[]): string {
    // Track what opponents have picked from discard
    const dangerousRanks = new Set<string>();
    const dangerousSuits = new Set<string>();

    // Cards recently discarded by opponents are less likely to be needed
    // Cards picked from discard by opponents indicate what they're collecting

    let safestId = hand[0].id;
    let safestScore = -Infinity;

    for (const card of hand) {
      if (card.isJoker || card.isWildJoker) continue;

      const without = hand.filter(c => c.id !== card.id);
      const handScore = this.evaluateHand(hand) - this.evaluateHand(without);

      // Safety: prefer discarding cards that opponents are unlikely to need
      let safety = 0;

      // Cards with rank/suit that opponents have discarded are safer
      if (!dangerousRanks.has(card.rank)) safety += 2;
      if (!dangerousSuits.has(card.suit)) safety += 1;

      // Middle cards (5-9) are more dangerous than edge cards
      const rankVal = RANK_VALUES[card.rank];
      if (rankVal >= 5 && rankVal <= 9) safety -= 2;

      // High point cards should be discarded sooner
      safety += CARD_POINTS[card.rank] * 0.2;

      const totalScore = safety - handScore;

      if (totalScore > safestScore) {
        safestScore = totalScore;
        safestId = card.id;
      }
    }

    return safestId;
  }

  /**
   * Update discard tracking for opponent analysis (Hard mode).
   */
  trackDiscard(playerId: string, card: Card): void {
    const existing = this.discardTracker.get(playerId) || [];
    existing.push(card);
    this.discardTracker.set(playerId, existing);
  }

  resetTracking(): void {
    this.discardTracker.clear();
  }
}
